/**
 * Monta o HTML de cada tela de acesso.
 *
 * **Retorna `string`, e o `innerHTML` recebe esse string.** Nao ha framework
 * neste aplicativo (PLANO 8.179), e a alternativa -- `createElement` campo por
 * campo -- e quatro vezes o codigo para a mesma tela.
 *
 * **Todo valor que vem de fora passa por `texto()`.** Nome, email, segredo de
 * TOTP, `detail` da API e URL de convite sao todos texto de outra pessoa dentro
 * de uma pagina que roda no aparelho dela. Sem escapar, um nome chamado
 * `<img onerror=...>` vira script, e o preco e a sessao -- que e exatamente o
 * que esta tela protege. **Nada de `innerHTML` direto com dado.**
 */

/**
 * Escapa texto para ir dentro de `innerHTML`. **Toda** interpolacao de dado que
 * vem de fora passa por aqui: nome, email, segredo de TOTP, `detail` da API e o
 * token de convite.
 *
 * **Escapamento explicito, e nao o truque do `textarea`.** A versao anterior usava
 * `textarea.innerHTML = valor; textarea.value`, que parece equivalente e **nao
 * escapava**: o texto saia cru e o `<b>` de um nome virava negrito de verdade. O
 * que pegou isso foi um teste com uma conta cujo nome tem `<b>` e `<script>` --
 * nenhuma tela de acesso mostra defeito assim, porque o resultado *parece*
 * correto. Um nome chamado `<img onerror=...>` seria a mesma linha de codigo.
 *
 * A ordem importa: `&` primeiro, senao as entidades que os outros replaces
 * produzem viram `&amp;lt;` e a tela mostra `&lt;` escrito por extenso.
 */
export function texto(valor: string): string {
  return valor
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&#39;");
}

const CAMPOS = `
  <label class="campo">
    <span class="campo__rotulo" id="rotulo-email">Email</span>
    <input id="email" name="email" type="email" inputmode="email"
           autocomplete="username" autocapitalize="none" spellcheck="false" required />
  </label>
`;

const SENHA = `
  <label class="campo">
    <span class="campo__rotulo" id="rotulo-senha">Senha</span>
    <input id="senha" name="senha" type="password" autocomplete="current-password" required />
  </label>
`;

const CODIGO = (rotulo: string, dica: string) => `
  <label class="campo">
    <span class="campo__rotulo" id="rotulo-codigo">${texto(rotulo)}</span>
    <input id="codigo" name="codigo" type="text" inputmode="numeric"
           autocomplete="one-time-code" pattern="[0-9]*" maxlength="8" required />
    <span class="campo__dica">${texto(dica)}</span>
  </label>
`;

const ERRO = `
  <p class="alerta alerta--erro" id="erro" hidden role="alert"></p>
`;

/** `/entrar`: email, senha e, opcionalmente, o codigo do segundo fator. */
export function telaDeEntrada(): string {
  return `
    <h2 class="titulo">Entrar</h2>
    <form id="forma" class="forma" novalidate>
      ${CAMPOS}
      ${SENHA}
      ${CODIGO("Codigo de 6 digitos", "So quem tem o autenticador precisa. Deixe vazio se nao tem.")}
      ${ERRO}
      <button type="submit" class="botao botao--principal" id="enviar">Entrar</button>
    </form>
    <p class="rodape"><a href="/recuperar">Perdi o celular</a></p>
  `;
}

/**
 * O involucro de todas as telas de acesso: centraliza e limita a largura.
 *
 * Uma tela de login que ocupa a largura toda tem o campo de senha a 4 cm da
 * borda em um tablet de 13 polegadas, e o botao a meia tela de altura.
 */
export function painel(html: string): string {
  return `<div class="painel">${html}</div>`;
}

/** `/convite/<token>`: o token vem da URL e nunca e reescrito para outra rota. */
export function telaDeConvite(token: string): string {
  return `
    <h2 class="titulo">Entrar na casa</h2>
    <form id="forma" class="forma" novalidate>
      <label class="campo">
        <span class="campo__rotulo" id="rotulo-nome">Como voce quer ser chamado</span>
        <input id="nome" name="nome" type="text" autocomplete="name" maxlength="200" required />
      </label>
      ${SENHA.replace('autocomplete="current-password"', 'autocomplete="new-password"')}
      <p class="campo__dica">A senha tem pelo menos 12 caracteres.</p>
      ${ERRO}
      <button type="submit" class="botao botao--principal" id="enviar">Criar conta</button>
    </form>
    <p class="rodape">Convite <code>${texto(token.slice(0, 8))}...</code></p>
  `;
}

/** `/recuperar`: email, senha e um codigo de recuperacao. */
export function telaDeRecuperacao(): string {
  return `
    <h2 class="titulo">Recuperar acesso</h2>
    <p class="nota">
      Um dos codigos de recuperacao, os de papel. Usar um reinicia o
      autenticador e derruba as outras sessoes.
    </p>
    <form id="forma" class="forma" novalidate>
      ${CAMPOS}
      ${SENHA}
      ${CODIGO("Codigo de recuperacao", "Dez caracteres, como no papel.")}
      ${ERRO}
      <button type="submit" class="botao botao--principal" id="enviar">Entrar</button>
    </form>
    <p class="rodape"><a href="/entrar">Voltar para entrar</a></p>
  `;
}

/**
 * `/totp`: o setup do segundo fator do admin, com o QR e o segredo em texto.
 *
 * **O QR e o caminho principal, e o texto e a saida de emergencia.** Digitar 32
 * caracteres base32 a mao e onde a transcricao erra -- e um caractere trocado
 * gera codigos errados para sempre, sem nada na tela dizendo por que.
 *
 * O QR e codificado no navegador a partir da `uri` que a API devolveu: o que a
 * camera le e a mesma `otpauth://` que o servidor gerou, e nao uma montagem
 * paralela que possa divergir dela.
 */
export function telaDeTotp(segredo: string, qr: string): string {
  return `
    <h2 class="titulo">Segundo fator</h2>
    <p class="nota">
      Abra o autenticador e aponte a camera para o codigo. Se nao der para
      escanear, digite o segredo que esta embaixo. Depois, confirme com o
      codigo que ele gerar.
    </p>
    <img class="qr" id="qr" src="${texto(qr)}" alt="Codigo QR do segundo fator" />
    <p class="segredo" id="segredo" data-valor="${texto(segredo)}">${texto(segredo)}</p>
    <form id="forma" class="forma" novalidate>
      ${CODIGO("Codigo do autenticador", "Seis digitos.")}
      ${ERRO}
      <button type="submit" class="botao botao--principal" id="enviar">Confirmar</button>
    </form>
  `;
}

/** Os dez codigos de recuperacao, uma unica vez. */
export function telaDeCodigos(codigos: string[], aviso: string): string {
  return `
    <h2 class="titulo">Codigos de recuperacao</h2>
    <p class="alerta alerta--aviso">${texto(aviso)}</p>
    <ul class="codigos" id="codigos">
      ${codigos.map((codigo) => `<li><code>${texto(codigo)}</code></li>`).join("")}
    </ul>
    <p class="nota">
      Sem papel e sem o autenticador, ninguem entra -- nem quem administer a casa.
    </p>
    <p class="rodape">
      <a class="botao botao--principal" href="/" id="continuar-para-a-casa">Salvei os codigos, voltar para a casa</a>
    </p>
  `;
}

export interface AlertaParaTela {
  id: string;
  kind: string;
  rotulo: string;
  title: string;
  body: string;
  criada_em: string;
  lido: boolean;
  status: string;
}

/** A central de alertas: a lista, a contagem e o botao de marcar tudo. */
export function telaDaCentral(
  alertas: AlertaParaTela[],
  naoLidos: number,
  recemMarcados: number,
): string {
  const lista = alertas
    .map(
      (alerta) => `
      <li class="linha ${alerta.lido ? "linha--lida" : ""}" data-alerta="${texto(alerta.id)}">
        <div class="linha__principal">
          <strong>${texto(alerta.title)}</strong>
          <span class="selo ${alerta.lido ? "" : "selo--sim"}">${texto(alerta.rotulo)}</span>
          ${alerta.status === "queued" ? '<span class="selo selo--nao">ainda nao saiu</span>' : ""}
        </div>
        <p class="nota">${texto(alerta.body)}</p>
        <div class="linha__principal">
          <span class="selo">${texto(quando(alerta.criada_em))}</span>
          ${
            alerta.lido
              ? '<span class="selo">lido</span>'
              : `<button type="button" class="botao" data-marcar-lido="${texto(alerta.id)}">Marcar como lido</button>`
          }
        </div>
      </li>`,
    )
    .join("");

  return `
    <h2 class="titulo">Central</h2>
    <p class="nota">
      ${
        naoLidos === 0
          ? "Nada para ler."
          : `${naoLidos} ${naoLidos === 1 ? "alerta para ler" : "alertas para ler"}.`
      }
      ${recemMarcados > 0 ? `<span id="marcados-recem">${recemMarcados} marcados nesta visita.</span>` : ""}
    </p>
    <p class="alerta alerta--erro" id="aviso-da-central" hidden role="alert"></p>
    <div class="linha__acoes">
      <button type="button" class="botao" id="marcar-todos" ${
        naoLidos === 0 ? "disabled" : ""
      }>Marcar todos como lidos</button>
    </div>
    <ul class="lista" id="lista-de-alertas">${
      lista === "" ? '<li class="vazio">Nada por aqui.</li>' : lista
    }</ul>
  `;
}

/** A casa nao respondeu: nem e "deslogado", nem e "erro". */
export function telaSemConexao(): string {
  return `
    <h2 class="titulo">A casa nao respondeu</h2>
    <p class="nota">
      O sinal nao chegou, ou o servidor da casa esta fora do ar. Isso nao e a
      sua conta: nada foi desconectado.
    </p>
    <p class="nota">
      Nao digite a senha aqui -- o que aparece nesta tela nao e a casa.
    </p>
    <button type="button" class="botao botao--principal" id="tentar-de-novo">Tentar de novo</button>
  `;
}

/**
 * Quando uma tela falha e nao ha o que fazer com o erro.
 *
 * **Existe para nao deixar o esqueleto estatico do `index.html` na tela.** Uma
 * excecao dentro do desenho nao trocava nada: o que ficava visivel era o texto
 * de carregamento do HTML, que parece que o app nao abriu -- e nao diz nada
 * sobre o que aconteceu. Uma tela que falha em silencio e pior que uma que
 * falha dizendo o que houve.
 */
export function telaDeFalha(mensagem: string): string {
  return `
    <h2 class="titulo">Nao deu para abrir esta tela</h2>
    <p class="alerta alerta--erro">${texto(mensagem)}</p>
    <p class="rodape"><a href="/">Voltar para o inicio</a></p>
  `;
}

/** A data, no formato que uma pessoa le: "12 de marco, as 14h30". */
export function quando(iso: string): string {
  const data = new Date(iso);
  if (Number.isNaN(data.getTime())) {
    return iso;
  }
  return new Intl.DateTimeFormat("pt-BR", {
    dateStyle: "short",
    timeStyle: "short",
  }).format(data);
}

// ------------------------------------------------------------------------- mapa

/**
 * A idade de um ponto, **sempre visivel**.
 *
 * **O item 6 e este numero.** Um ponto de tres horas atras desenhado como pino
 * normal diz "a Ana esta aqui" quando a Ana esta em outro estado ha tres horas --
 * e e a leitura que faz alguem ligar para o hospital. Por isso a idade aparece
 * em **todas** as tres pecas onde o ponto aparece: o rotulo do marcador, a lista
 * de pessoas e o popup.
 *
 * **O limite e de uma hora**, e nao um detalhe de estilo: abaixo disso o ponto e
 * recente; acima, a tela diz que e antigo **e muda o desenho**. O
 * `velho_apos_s` e uma constante e nao um numero solto porque a lista e o mapa
 * precisam concordar com a linha -- se o mapa diz "antigo" e a lista nao, o
 * item esta entregue pela metade.
 */
export const VELHO_APOS_S = 3600;

/** A idade em palavras de gente, e se ela e antiga. */
export function idadeLegivel(idadeS: number): { texto: string; velho: boolean } {
  if (idadeS < 90) {
    return { texto: "agora", velho: false };
  }
  if (idadeS < VELHO_APOS_S) {
    return { texto: `ha ${Math.round(idadeS / 60)} min`, velho: false };
  }
  const horas = Math.round(idadeS / VELHO_APOS_S);
  return { texto: `ha ${horas} h`, velho: true };
}

export interface PessoaParaTela {
  device_id: string;
  nome: string;
  rotulo: string;
  /** "agora", "12 min", "ha 3 h". Nunca vazio, nunca "agora" sem ser. */
  idade: string;
  /** `true` quando a posicao passou de uma hora. A tela muda o desenho. */
  velho: boolean;
  precisao: string;
  bateria: string;
  coletado: string;
  /** O aparelho e **meu**: so o meu tem botao de pausa e **nao** tem de pedir. */
  meu: boolean;
  pausado: boolean;
  semPosicao: boolean;
  /** Ha um pedido vivo para este aparelho: a resposta esta a caminho. */
  pedindo: boolean;
  /**
   * O `sms:` que o **servidor** montou, so no iPhone e so depois de pedir.
   *
   * **A tela nao monta esta URL.** O telefone esta no cadastro do aparelho e a
   * frase-codigo esta no pedido; nenhum dos dois esta no estado da tela, e uma
   * URL montada aqui sairia com o telefone errado em silencio.
   */
  linkDaMensagem: string | null;
}

export interface PedidoParaTela {
  id: string;
  quem: string;
  sobre: string;
  quando: string;
  desfecho: string;
  /** O pedido e sobre o meu aparelho -- e a resposta a "quem pediu sua posicao". */
  sobreMeu: boolean;
}

/**
 * O mapa: os pinos, quem e quem, e quem pediu o que.
 *
 * **A lista de pessoas e a fonte da verdade da tela, e o mapa e a mesma coisa
 * desenhada.** Um marcador sem lista deixa quem nao distingue as cores sem
 * informacao nenhuma; uma lista sem mapa nao situa ninguem. As duas leem a mesma
 * estrutura, entao nao podem discordar sobre a idade de um ponto.
 */
export function telaDoMapa(pessoas: PessoaParaTela[], pedidos: PedidoParaTela[]): string {
  const linhas = pessoas
    .map(
      (pessoa) => `
      <li class="linha" data-pessoa="${texto(pessoa.device_id)}" data-velho="${pessoa.velho ? "sim" : "nao"}">
        <div class="linha__principal">
          <strong>${texto(pessoa.nome)}</strong>
          <span class="selo">${texto(pessoa.rotulo)}</span>
          ${
            pessoa.semPosicao
              ? '<span class="selo selo--nao" data-idade="sem-posicao">sem posicao</span>'
              : `<span class="selo ${pessoa.velho ? "selo--nao" : "selo--sim"}" data-idade="${
                  pessoa.velho ? "antiga" : "recente"
                }">${texto(pessoa.idade)}</span>`
          }
        </div>
        <p class="nota">
          ${
            pessoa.semPosicao
              ? "Nenhum ponto recebido deste aparelho."
              : `coletado ${texto(pessoa.coletado)} &middot; precisao ${texto(
                  pessoa.precisao,
                )} &middot; bateria ${texto(pessoa.bateria)}`
          }
        </p>
        ${
          pessoa.meu
            ? `<div class="linha__principal">
                 <span class="selo">${pessoa.pausado ? "voce pausou este aparelho" : "compartilhamento ligado"}</span>
                 <button type="button" class="botao" data-pausar="${texto(
                   pessoa.device_id,
                 )}" data-pausado="${pessoa.pausado ? "sim" : "nao"}">
                   ${pessoa.pausado ? "Voltar a compartilhar" : "Pausar compartilhamento"}
                 </button>
               </div>`
            : `<div class="linha__principal">
                 ${
                   pessoa.pedindo
                     ? '<span class="selo selo--sim" data-esperando="sim">esperando resposta</span>'
                     : `<button type="button" class="botao" data-pedir="${texto(
                         pessoa.device_id,
                       )}">Pedir posicao</button>`
                 }
                 ${
                   pessoa.linkDaMensagem === null
                     ? ""
                     : `<a class="botao" id="pedir-por-mensagem" href="${texto(
                         pessoa.linkDaMensagem,
                       )}">Pedir por mensagem</a>`
                 }
               </div>`
        }
      </li>`,
    )
    .join("");

  const linhasDePedidos = pedidos
    .map(
      (pedido) => `
      <li class="linha" data-pedido="${texto(pedido.id)}">
        <div class="linha__principal">
          <strong>${texto(pedido.quem)}</strong>
          <span class="selo ${pedido.sobreMeu ? "selo--sim" : ""}">${texto(pedido.desfecho)}</span>
        </div>
        <p class="nota">${texto(pedido.quando)} &middot; sobre ${texto(pedido.sobre)}</p>
      </li>`,
    )
    .join("");

  return `
    <h2 class="titulo">Mapa</h2>
    <p class="nota">
      A posicao de cada aparelho, com a hora em que foi coletada. Um ponto velho
      aparece com a idade dele &mdash; nunca como se fosse de agora.
    </p>
    <p class="alerta alerta--erro" id="aviso-do-mapa" hidden role="alert"></p>
    <div id="mapa" class="mapa" role="application" aria-label="Mapa da casa"></div>
    <ul class="lista" id="lista-de-pessoas">${
      linhas === "" ? '<li class="vazio">Nenhum aparelho.</li>' : linhas
    }</ul>
    <h3 class="titulo">Quem pediu e quando</h3>
    <ul class="lista" id="lista-de-pedidos">${
      linhasDePedidos === "" ? '<li class="vazio">Ninguem perguntou nada ainda.</li>' : linhasDePedidos
    }</ul>
  `;
}

export interface SessaoParaTela {
  id: string;
  criada_em: string;
  ultimo_uso_em: string;
  expira_em: string;
  esta_atual: boolean;
}

export interface AparelhoParaTela {
  id: string;
  platform: string;
  label: string;
  mqtt_username: string;
  tem_credencial_mqtt: boolean;
  tem_token_de_atalhos: boolean;
  trigger_email: string | null;
  trigger_phone: string | null;
  compartilhamento_pausado: boolean;
  revogado_em: string | null;
  push_configurado: boolean;
}

function distintivo(condicao: boolean, rotulo: string): string {
  return condicao ? `<span class="selo selo--sim">${texto(rotulo)}</span>` : "";
}

/** A tela de Aparelhos: sessoes, aparelhos e o que cada um tem ou nao tem. */
export function telaDeAparelhos(
  sessoes: SessaoParaTela[],
  aparelhos: AparelhoParaTela[],
): string {
  const listaDeSessoes = sessoes
    .map(
      (sessao) => `
      <li class="linha" data-sessao="${texto(sessao.id)}">
        <div class="linha__principal">
          <strong>${sessao.esta_atual ? "Esta sessao" : "Outra sessao"}</strong>
          ${distintivo(sessao.esta_atual, "aqui")}
        </div>
        <dl class="pares">
          <dt>Entrou</dt><dd>${texto(quando(sessao.criada_em))}</dd>
          <dt>Ultimo uso</dt><dd>${texto(quando(sessao.ultimo_uso_em))}</dd>
          <dt>Expira</dt><dd>${texto(quando(sessao.expira_em))}</dd>
        </dl>
        ${
          sessao.esta_atual
            ? ""
            : `<button type="button" class="botao botao--perigo" data-revogar-sessao="${texto(sessao.id)}">Sair desta sessao</button>`
        }
      </li>`,
    )
    .join("");

  const listaDeAparelhos = aparelhos
    .map(
      (aparelho) => `
      <li class="linha" data-aparelho="${texto(aparelho.id)}">
        <div class="linha__principal">
          <strong>${texto(aparelho.label)}</strong>
          <span class="selo">${texto(aparelho.platform)}</span>
          ${distintivo(aparelho.tem_credencial_mqtt, "credencial MQTT")}
          ${distintivo(aparelho.tem_token_de_atalhos, "token de atalhos")}
          ${distintivo(aparelho.push_configurado, "notificacoes")}
          ${distintivo(aparelho.compartilhamento_pausado, "compartilhamento pausado")}
          ${aparelho.revogado_em === null ? "" : '<span class="selo selo--nao">revogado</span>'}
        </div>
        <dl class="pares">
          <dt>Usuario MQTT</dt><dd><code>${texto(aparelho.mqtt_username)}</code></dd>
          <dt>E-mail do atalho</dt><dd>${aparelho.trigger_email === null ? "—" : texto(aparelho.trigger_email)}</dd>
          <dt>Telefone do atalho</dt><dd>${aparelho.trigger_phone === null ? "—" : texto(aparelho.trigger_phone)}</dd>
        </dl>
        <div class="linha__acoes">
          <button type="button" class="botao" data-gerar-mqtt="${texto(aparelho.id)}">Gerar senha MQTT</button>
          <button type="button" class="botao" data-atalhos="${texto(aparelho.id)}">Configurar atalhos</button>
          <button type="button" class="botao" data-push="${texto(aparelho.id)}">Ativar notificacoes</button>
          <button type="button" class="botao botao--perigo" data-revogar-aparelho="${texto(aparelho.id)}">Revogar aparelho</button>
        </div>
      </li>`,
    )
    .join("");

  return `
    <h2 class="titulo">Aparelhos</h2>
    <p class="nota">
      Quem tem acesso a esta casa, e o que cada aparelho pode fazer aqui.
    </p>

    <section class="secao">
      <h3 class="secao__titulo">Sessoes</h3>
      <ul class="lista" id="lista-de-sessoes">${listaDeSessoes}</ul>
    </section>

    <section class="secao">
      <h3 class="secao__titulo">Aparelhos</h3>
      <ul class="lista" id="lista-de-aparelhos">${listaDeAparelhos}</ul>

      <form id="forma-novo-aparelho" class="forma" novalidate>
        <h4 class="secao__subtitulo">Cadastrar um aparelho</h4>
        <label class="campo">
          <span class="campo__rotulo">Nome</span>
          <input id="rotulo-do-aparelho" type="text" maxlength="120" required />
        </label>
        <label class="campo">
          <span class="campo__rotulo">Tipo</span>
          <select id="plataforma-do-aparelho">
            <option value="android">Android</option>
            <option value="ios">iPhone</option>
          </select>
        </label>
        <button type="submit" class="botao botao--principal" id="cadastrar-aparelho">Cadastrar</button>
      </form>
    </section>

    <div id="area-da-credencial" hidden></div>
  `;
}

/** A senha MQTT, mostrada **uma unica vez**. */
export function telaDaCredencial(
  usuario: string,
  senha: string,
  aviso: string,
): string {
  return `
    <div class="cobertor" id="cobertor" role="dialog" aria-modal="true" aria-labelledby="titulo-da-credencial">
      <div class="painel">
        <h3 class="titulo" id="titulo-da-credencial">Senha MQTT</h3>
        <p class="alerta alerta--aviso">${texto(aviso)}</p>
        <dl class="pares">
          <dt>Usuario</dt><dd><code id="usuario-mqtt">${texto(usuario)}</code></dd>
          <dt>Senha</dt><dd><code id="senha-mqtt">${texto(senha)}</code></dd>
        </dl>
        <button type="button" class="botao botao--principal" id="fechar-credencial">Ja anotei</button>
      </div>
    </div>
  `;
}

/** O prompt de reautenticacao, em cima da tela que a pessoa queria ver. */
export function telaDeReautenticacao(motivo: string): string {
  return `
    <div class="cobertor" id="cobertor" role="dialog" aria-modal="true" aria-labelledby="titulo-da-reauth">
      <div class="painel">
        <h3 class="titulo" id="titulo-da-reauth">Confirme de novo</h3>
        <p class="nota">${texto(motivo)}</p>
        <form id="forma-reauth" class="forma" novalidate>
          <label class="campo">
            <span class="campo__rotulo">Senha</span>
            <input id="senha-reauth" type="password" autocomplete="current-password" required />
          </label>
          <label class="campo">
            <span class="campo__rotulo">Codigo de 6 digitos</span>
            <input id="codigo-reauth" type="text" inputmode="numeric"
                   autocomplete="one-time-code" maxlength="8" />
            <span class="campo__dica">So se voce tem o autenticador.</span>
          </label>
          <p class="alerta alerta--erro" id="erro-reauth" hidden role="alert"></p>
          <div class="linha__acoes">
            <button type="submit" class="botao botao--principal" id="confirmar-reauth">Confirmar</button>
            <button type="button" class="botao" id="cancelar-reauth">Cancelar</button>
          </div>
        </form>
      </div>
    </div>
  `;
}

/** O formulario de atalhos, dentro da linha do aparelho. */
export function telaDeAtalhos(
  deviceId: string,
  email: string | null,
  telefone: string | null,
): string {
  return `
    <form class="forma forma--dentro" data-atalhos-de="${texto(deviceId)}" novalidate>
      <label class="campo">
        <span class="campo__rotulo">E-mail que recebe o pedido</span>
        <input id="email-do-atalho" type="email" value="${email === null ? "" : texto(email)}" />
      </label>
      <label class="campo">
        <span class="campo__rotulo">Telefone que recebe o pedido</span>
        <input id="telefone-do-atalho" type="tel" value="${telefone === null ? "" : texto(telefone)}" />
      </label>
      <div class="linha__acoes">
        <button type="submit" class="botao botao--principal" id="salvar-atalhos">Salvar e gerar token</button>
        <button type="button" class="botao" id="cancelar-atalhos">Cancelar</button>
      </div>
    </form>
  `;
}

/**
 * A casa: a porta de entrada, com as telas como alvos grandes.
 *
 * **Era um esboço.** Dizia *"as telas de financas entram nos proximos itens"* —
 * falso, porque as telas existem — e listava os destinos numa fileira de links
 * separados por `&middot;` no rodapé. Quem abria o app instalado via um aviso de
 * que nada existia, e alvos do tamanho da palavra, num canto da tela.
 *
 * **A ordem é a do uso, e não a alfabética.** O mapa responde "onde estão as
 * pessoas", que é a pergunta de todo dia; a central vem depois, porque alerta é
 * exceção; e aparelhos é configuração — quem mexe ali já sabe que quer mexer.
 *
 * **Com o segundo fator pendente, a tela mostra só o caminho de saída.** A API
 * bloqueia tudo o mais enquanto o admin não confirma, então oferecer mapa e
 * central ao lado do aviso seria oferecer dois alvos que respondem `403` — e o
 * app agora manda esse `403` de volta para cá, o que viraria um vaivém.
 */
export function telaDaCasa(nome: string, isAdmin: boolean, precisaTotp: boolean): string {
  const conta = `
    <section class="secao">
      <h3 class="secao__titulo">Conta</h3>
      <ul class="resumo">
        <li><span>Administrador</span><strong>${isAdmin ? "sim" : "nao"}</strong></li>
        <li>
          <span>Segundo fator</span>
          <strong data-totp="${precisaTotp ? "sim" : "nao"}">${precisaTotp ? "pendente" : "confirmado"}</strong>
        </li>
      </ul>
      <p class="rodape"><a href="/sair" id="sair">Sair</a></p>
    </section>
  `;

  if (precisaTotp) {
    return `
      <h2 class="titulo">Ola, ${texto(nome)}</h2>
      <p class="alerta alerta--aviso">
        O segundo fator ainda nao foi confirmado. Ate confirmar o codigo do
        autenticador, a casa so responde as telas de configuracao.
      </p>
      <p class="rodape">
        <a class="botao botao--principal" href="/totp" id="ir-para-o-totp">Confirmar o segundo fator</a>
      </p>
      ${conta}
    `;
  }

  return `
    <h2 class="titulo">Ola, ${texto(nome)}</h2>
    <nav class="lista" id="telas">
      <a class="atalho" href="/mapa" id="ir-para-o-mapa">
        <strong>Mapa</strong>
        <span>Onde estao as pessoas da casa, e ha quanto tempo cada posicao foi vista.</span>
      </a>
      <a class="atalho" href="/alertas" id="ir-para-a-central">
        <strong>Central</strong>
        <span>Os alertas que a casa mandou: cerca, saldo, vencimento e backup.</span>
      </a>
      <a class="atalho" href="/aparelhos" id="ir-para-os-aparelhos">
        <strong>Aparelhos</strong>
        <span>Sessoes abertas, aparelhos cadastrados, credencial MQTT e notificacoes.</span>
      </a>
    </nav>
    ${conta}
  `;
}